import { createApi, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { apiPath, baseUrl } from 'config'
import {
  CreatePoolRequest,
  DataChartPoolApy,
  DataChartPoolApyRequest,
  DataChartPoolPrice,
  DataChartPoolPriceRequest,
  DepositWithdrawalAsset,
  EditPoolRequest,
  GetPoolsPaginationResponse,
  GetPoolsTransformResponse,
  OperatorAuthorizationRequest,
  OperatorStatusResponse,
  OrderByDate,
  Pool,
  PoolAssetChangeRequest,
  PoolType,
  StatusPool,
} from 'types'

import { PoolData, setPool, setPoolData, setPoolDataNoAUM, setPoolStatus } from 'store/reducers-common/Pool/slice.ts'
import { getSignature } from 'utils/getSignature'
import { sortByAssetAllocation } from 'utils/sortByAssetAllocation'

import { setFundPoolsData } from '../Fund/slice'

import { fetchPools } from './fetchPools'
import { PoolDataRequest, resolvePoolDataRequest } from './poolDataRequest'

export const toPoolData = (pool: Pool): PoolData => ({
  assetsAllocation: sortByAssetAllocation(pool.assetAllocation, pool.poolAddress),
  chainIds: pool.chainIds ?? [],
  createdAt: pool.createdAt,
  customers: pool.customers,
  dayReturn: pool.dayReturn,
  decimals: pool.decimals,
  description: pool.description,
  fees: pool.fees,
  fundAddress: pool.fundAddress,
  fundShare: pool.fundShare,
  isPriceValid: pool.isPriceValid,
  isRWA: pool.isRWA,
  limits: pool.limits,
  maxDrawdown: pool.maxDrawdown,
  monthReturn: pool.monthReturn,
  netTargetApy: pool.netTargetApy,
  revenue: pool.revenue,
  riskFactor: pool.riskFactor,
  sevenDayApy: pool.sevenDayApy,
  sixMonthReturn: pool.sixMonthReturn,
  sixMonthReturnEst: pool.sixMonthReturnEst,
  strategyLaunched: pool.strategyLaunched,
  targetApy: pool.targetApy,
  threeMonthReturn: pool.threeMonthReturn,
  tokenPrice: pool.poolMetric.tokenPrice,
  totalDeposited: pool.totalDeposited,
  valueManaged: pool.valueManaged,
  vaultType: pool.vaultType,
  weekReturn: pool.weekReturn,
  yearReturn: pool.yearReturn,
  yearReturnEst: pool.yearReturnEst,
  yearToDate: pool.yearToDate,
})

export const queryPools = createApi({
  baseQuery: fetchBaseQuery({ baseUrl }),
  endpoints: (builder) => ({
    deletePool: builder.mutation<boolean, string>({
      invalidatesTags: ['TablePools'],
      queryFn: async (poolId, _api, _extraOptions, baseQuery) => {
        const url = `${apiPath.pool}/${poolId}`

        const { deadline, signature } = await getSignature({
          primaryType: 'DeletePool',
          url,
        })

        const result = await baseQuery({
          headers: { deadline: `${deadline}`, signature },
          method: 'DELETE',
          url,
        })

        if (!result.data) {
          return { error: result.error as FetchBaseQueryError }
        }

        return { data: result.data as boolean }
      },
    }),
    getAllPools: builder.query<
      GetPoolsPaginationResponse,
      {
        fundAddress?: string
        limit?: number
        orderByDate?: OrderByDate
        page?: number
        poolAddresses?: string[]
        status?: StatusPool[]
      }
    >({
      providesTags: ['GetPools'],
      query: ({ fundAddress, limit, orderByDate, page, poolAddresses, status }) => {
        let url = `${apiPath.pool}?limit=${limit ?? 15}`

        if (status && status.length) {
          url += `&${status.map((s) => `status=${s}`).join('&')}`
        }

        if (poolAddresses && poolAddresses.length) {
          url += `&${poolAddresses.map((s) => `poolAddresses=${s}`).join('&')}`
        }

        if (orderByDate) {
          url += `&orderByDate=${orderByDate}`
        }

        if (page) {
          url += `&page=${page}`
        }

        if (fundAddress) {
          url += `&fundAddress=${fundAddress}`
        }

        return url
      },
    }),
    getDataPoolApy: builder.query<DataChartPoolApy, DataChartPoolApyRequest>({
      keepUnusedDataFor: 60 * 60,
      query: ({ apyIntervalDays = 7, endDate, poolAddress, rawTokenPrices = false, startDate }) =>
        `${apiPath.pool}/${poolAddress}/apy?startDate=${startDate}&endDate=${endDate}&apyIntervalDays=${apyIntervalDays}&rawTokenPrices=${rawTokenPrices}`,
    }),
    getDataPoolPrice: builder.query<DataChartPoolPrice, DataChartPoolPriceRequest>({
      keepUnusedDataFor: 60 * 60,
      query: ({ endDate, interval, poolAddress, rawTokenPrices = false, startDate }) =>
        `${apiPath.pool}/${poolAddress}/price?interval=${interval}&startDate=${startDate}&endDate=${endDate}&rawTokenPrices=${rawTokenPrices}`,
    }),
    getFundTablePools: builder.query<
      GetPoolsTransformResponse,
      {
        fundAddress: string
        limit?: number
        orderByDate?: OrderByDate
        page?: number
        status?: StatusPool[]
        type?: PoolType
      }
    >({
      providesTags: ['TablePools'],
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const res = await fetchPools(arg, _api, _extraOptions, baseQuery)
        _api.dispatch(setFundPoolsData(res.data))
        return res
      },
    }),
    getPool: builder.query<{ pool: Pool }, { endDate: string; poolId: string; startDate: string } | string>({
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(
            setPool({
              address: data.pool.poolAddress,
              availableValueTokens: data.pool.availableValueTokens,
              decimals: data.pool.decimals,
              id: data.pool.id,
              minClientTier: data.pool.minClientTier,
            }),
          )
          dispatch(setPoolData(toPoolData(data.pool)))
          dispatch(setPoolStatus([{ id: data.pool.id, status: data.pool.status }]))
        } catch {
          // RTK Query keeps the request error in hook state; there is no side effect to run here.
        }
      },
      providesTags: ['GetPool'],
      query: (params) =>
        typeof params === 'string'
          ? `${apiPath.pool}/${params}`
          : `${apiPath.pool}/${params.poolId}?startDate=${params.startDate}&endDate=${params.endDate}`,
    }),
    getPoolData: builder.query<{ pool: Pool }, PoolDataRequest>({
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { preserveAum } = resolvePoolDataRequest(request)
          const { data } = await queryFulfilled
          const poolData = toPoolData(data.pool)

          dispatch(preserveAum ? setPoolDataNoAUM(poolData) : setPoolData(poolData))
          dispatch(setPoolStatus([{ id: data.pool.id, status: data.pool.status }]))
        } catch {
          // RTK Query keeps the request error in hook state; there is no side effect to run here.
        }
      },
      providesTags: ['GetPool'],
      query: (request) => {
        const { poolId } = resolvePoolDataRequest(request)
        const rawTokenPrices = import.meta.env.VITE_APP === 'funds' ? '?rawTokenPrices=true' : ''
        return `${apiPath.pool}/${poolId}${rawTokenPrices}`
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}:${resolvePoolDataRequest(queryArgs).poolId}`,
    }),
    getPoolOperatorStatus: builder.query<
      OperatorStatusResponse,
      Pick<OperatorAuthorizationRequest, 'owner' | 'poolAddress'>
    >({
      queryFn: async ({ poolAddress }, _api, _extraOptions, baseQuery) => {
        const url = apiPath.poolOperator(poolAddress)
        const { deadline, signature } = await getSignature({ primaryType: 'GetOperatorStatus', url })
        const result = await baseQuery({ headers: { deadline: `${deadline}`, signature }, url })

        return result.error
          ? { error: result.error as FetchBaseQueryError }
          : { data: result.data as OperatorStatusResponse }
      },
    }),
    getPoolPrivate: builder.query<Pool, string>({
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(
            setPool({
              address: data.poolAddress,
              availableValueTokens: data.availableValueTokens,
              decimals: data.decimals,
              id: data.id,
              minClientTier: data.minClientTier,
            }),
          )
        } catch (e) {
          throw new Error(`Get pool query error: ${e}`)
        }
      },
      providesTags: ['GetPool'],
      queryFn: async (payload, _api, _extraOptions, baseQuery) => {
        const url = `${apiPath.pool}/${payload}/private`

        const { deadline, signature } = await getSignature({
          primaryType: 'GetPoolPrivate',
          url,
        })

        const result = await baseQuery({
          headers: { deadline: `${deadline}`, signature },
          url,
        })
        if (!result.data) {
          return { error: result.error as FetchBaseQueryError }
        }

        return { data: result.data as Pool }
      },
    }),
    postCreatePool: builder.mutation<Pool, CreatePoolRequest>({
      invalidatesTags: ['GetPools'],
      queryFn: async (payload, _api, _extraOptions, baseQuery) => {
        const { deadline, signature } = await getSignature({
          payload: { ...payload, description: JSON.stringify(payload.description) },
          primaryType: 'CreatePool',
          url: apiPath.pool,
        })

        const result = await baseQuery({
          body: { ...payload, description: JSON.stringify(payload.description) },
          headers: { deadline: `${deadline}`, signature },
          method: 'POST',
          url: apiPath.pool,
        })
        if (!result.data) {
          return { error: result.error as FetchBaseQueryError }
        }

        return { data: result.data as Pool }
      },
    }),
    postEditPool: builder.mutation<Pool, EditPoolRequest>({
      invalidatesTags: ['GetPools', 'GetPool'],
      queryFn: async (payload, _api, _extraOptions, baseQuery) => {
        const {
          availableValueTokens,
          description,
          logo,
          name,
          notificationEmail,
          notificationSettings,
          poolId,
          riskFactor,
          targetApy,
          type,
          withdraw_lockup: withdrawLockup,
        } = payload
        const url = `${apiPath.pool}/${poolId}`
        const { deadline, signature } = await getSignature({
          payload: { ...payload, description: JSON.stringify(description) },
          primaryType: 'EditPool',
          url,
        })

        const result = await baseQuery({
          body: {
            availableValueTokens,
            description: JSON.stringify(description),
            logo,
            name,
            notificationEmail,
            notificationSettings,
            riskFactor,
            targetApy,
            type,
            withdraw_lockup: withdrawLockup,
          },
          headers: { deadline: `${deadline}`, signature },
          method: 'POST',
          url,
        })
        if (!result.data) {
          return { error: result.error as FetchBaseQueryError }
        }

        return { data: result.data as Pool }
      },
    }),
    postPoolAssetChange: builder.mutation<DepositWithdrawalAsset, PoolAssetChangeRequest>({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const url = `${apiPath.assetChange(arg.poolId)}`

        const result = await baseQuery({
          body: arg.poolAssetChange,
          method: 'POST',
          url,
        })
        if (!result.data) {
          return { error: result.error as FetchBaseQueryError }
        }

        return { data: result.data as DepositWithdrawalAsset }
      },
    }),
    postPoolOperator: builder.mutation<void, OperatorAuthorizationRequest>({
      query: ({ poolAddress, ...body }) => ({
        body,
        method: 'POST',
        url: apiPath.poolOperator(poolAddress),
      }),
    }),
  }),
  reducerPath: 'pools',
  tagTypes: ['GetPools', 'TablePools', 'GetPool'],
})

export const {
  useDeletePoolMutation,
  useGetAllPoolsQuery,
  useGetDataPoolApyQuery,
  useGetDataPoolPriceQuery,
  useGetFundTablePoolsQuery,
  useGetPoolDataQuery,
  useGetPoolOperatorStatusQuery,
  useGetPoolPrivateQuery,
  useGetPoolQuery,
  useLazyGetDataPoolApyQuery,
  useLazyGetDataPoolPriceQuery,
  useLazyGetPoolDataQuery,
  useLazyGetPoolOperatorStatusQuery,
  useLazyGetPoolQuery,
  usePostCreatePoolMutation,
  usePostEditPoolMutation,
  usePostPoolAssetChangeMutation,
  usePostPoolOperatorMutation,
} = queryPools
